import { type ChangeEvent, memo, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@air/primitive-button';

import { WorkspaceAgentGuidedVariantOutputOptionsDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantOutputOptionsDock';
import { useWorkspaceAgentPanelBodyElement } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelBodyElementContext';
import {
  formatWorkspaceAgentGuidedVariantOutputSummary,
  getWorkspaceAgentGuidedVariantOutputFormats,
  getWorkspaceAgentGuidedVariantOutputOptionsLabel,
  isWorkspaceAgentGuidedVariantOutputFormat,
  resolveWorkspaceAgentGuidedVariantOutput,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';

export type WorkspaceAgentGuidedVariantOutputOptionsProps = {
  allowsFormat: boolean;
  allowsJpeg: boolean;
  /** Trailing control on the same row, such as Discard. */
  children: ReactNode;
  onChange: (output: WorkspaceAgentGuidedVariantOutput) => void;
  onOpenChange: (isOpen: boolean) => void;
  output: WorkspaceAgentGuidedVariantOutput;
};

export const WorkspaceAgentGuidedVariantOutputOptions = memo(
  ({
    allowsFormat,
    allowsJpeg,
    children,
    onChange,
    onOpenChange,
    output,
  }: WorkspaceAgentGuidedVariantOutputOptionsProps) => {
    const titleId = useId();
    const suffixInputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { element: panelBody } = useWorkspaceAgentPanelBodyElement();
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<WorkspaceAgentGuidedVariantOutput>(output);
    const summary = formatWorkspaceAgentGuidedVariantOutputSummary(output);
    const formats = getWorkspaceAgentGuidedVariantOutputFormats({ allowsJpeg });
    const title = getWorkspaceAgentGuidedVariantOutputOptionsLabel({ allowsFormat });

    const handleOpen = useCallback(() => {
      if (isOpen) {
        return;
      }

      setDraft(resolveWorkspaceAgentGuidedVariantOutput(output, { allowsFormat, allowsJpeg }));
      setIsOpen(true);
      onOpenChange(true);
    }, [allowsFormat, allowsJpeg, isOpen, onOpenChange, output]);

    const handleCancel = useCallback(() => {
      setIsOpen(false);
      onOpenChange(false);
    }, [onOpenChange]);

    const handleContinue = useCallback(() => {
      onChange(
        resolveWorkspaceAgentGuidedVariantOutput(
          { format: draft.format, suffix: draft.suffix.trim() },
          { allowsFormat, allowsJpeg },
        ),
      );
      setIsOpen(false);
      onOpenChange(false);
    }, [allowsFormat, allowsJpeg, draft.format, draft.suffix, onChange, onOpenChange]);

    const handleSuffixChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      const suffix = event.currentTarget.value;

      setDraft((current) => ({ ...current, suffix }));
    }, []);

    const handleFormatChange = useCallback((value: string) => {
      if (!isWorkspaceAgentGuidedVariantOutputFormat(value)) {
        return;
      }

      setDraft((current) => ({ ...current, format: value }));
    }, []);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      setDraft((current) => resolveWorkspaceAgentGuidedVariantOutput(current, { allowsFormat, allowsJpeg }));
    }, [allowsFormat, allowsJpeg, isOpen]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      suffixInputRef.current?.focus({ preventScroll: true });
    }, [isOpen]);

    const dock = (
      <WorkspaceAgentGuidedVariantOutputOptionsDock
        allowsFormat={allowsFormat}
        draft={draft}
        formats={formats}
        onCancel={handleCancel}
        onContinue={handleContinue}
        onFormatChange={handleFormatChange}
        onSuffixChange={handleSuffixChange}
        panelBody={panelBody}
        suffixInputRef={suffixInputRef}
        title={title}
        titleId={titleId}
        triggerRef={triggerRef}
      />
    );
    const portaledDock = panelBody ? createPortal(dock, panelBody) : dock;

    return (
      <>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <Button
            appearance="ghost"
            aria-expanded={isOpen}
            ref={triggerRef}
            aria-haspopup="dialog"
            className="min-w-0 max-w-full shrink justify-start overflow-hidden"
            color="gray"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_OPTIONS"
            onClick={handleOpen}
            size="small"
            type="button"
          >
            <span className="flex min-w-0 items-center overflow-hidden">
              <span className="shrink-0">{title}</span>
              {summary ? (
                <span
                  className="ml-1 min-w-0 truncate font-normal text-gray-9"
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_SUMMARY"
                  title={summary}
                >
                  {summary}
                </span>
              ) : null}
            </span>
          </Button>
          <div className="shrink-0">{children}</div>
        </div>
        {isOpen ? portaledDock : null}
      </>
    );
  },
);

WorkspaceAgentGuidedVariantOutputOptions.displayName = 'WorkspaceAgentGuidedVariantOutputOptions';
